import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import {
  ArchiveIcon,
  DeleteIcon,
  FileIcon,
  PinFilledIcon,
  PinIcon,
  RenameIcon,
  StarFilledIcon,
} from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import { API } from 'src/api';
import { useSidebarStore } from 'src/components/sidebar/store';
import { routeToPrimaryViewURL, useFileStore } from 'src/features/files';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';
import { useAuth } from 'src/shared/hooks/use-auth';

import { useFileExploreStore } from '../store';

const { useCallback, useState } = React;

export const FileActions = (): JSX.Element => {
  const { closeExploreMenu } = useFileExploreStore();
  const { setIsRenameFileOpened } = useFileStore();
  const router = useRouter();
  const fileId = router.query.slug as string;
  const viewId = router.query.view as string;
  const orgId = router.query.orgId as string;
  const mode = router.query.mode as string;
  const onPinFile = useFileStore((s) => s.onPinFile);
  const onFavoriteFile = useFileStore((s) => s.onFavoriteFile);
  const fetchSidebar = useSidebarStore((s) => s.fetchSidebar);
  const onDeleteView = useFileStore((s) => s.onDeleteView);
  const [openSnackbar] = useSnackbar();
  const [isDeleteFilesModalOpen, setisDeleteFilesModalOpen] = useState(false);
  const fetchUserDetails = useAuth((s) => s.fetchUserDetails);
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  const { isReadOnly } = useFileAccess();

  const handleOnClose = () => {
    closeExploreMenu();
  };

  const handlePinFile = async (pin: boolean): Promise<any> => {
    const response: any = await onPinFile?.(fileId, pin);
    if (response) {
      fetchSidebar(mode);
      closeExploreMenu();
      openSnackbar({
        text: `${currentFile?.name || 'File'} ${pin ? 'pinned to' : 'unpinned from'} sidebar`,
        snackType: 'standard',
        position: 'bottom-center',
        duration: 1500,
      });
    }
  };

  const handleFavoriteFile = async (favorite: boolean): Promise<any> => {
    const response: any = await onFavoriteFile?.(fileId, favorite);
    if (response) {
      closeExploreMenu();
      openSnackbar({
        text: `${currentFile?.name || 'File'} ${
          favorite ? 'marked as favorite' : 'removed from favorites'
        }`,
        snackType: 'standard',
        position: 'bottom-center',
        duration: 1500,
      });
    }
  };

  const handleDeleteView = () => {
    closeExploreMenu();
    const response: any = onDeleteView({ fileId, viewId });
    if (response && currentFile) {
      router.push(routeToPrimaryViewURL({ currentFile, orgId, mode }));
      const deletedView = currentFile?.internalFiles?.find((view) => view.id === viewId);
      const deletedType = deletedView?.view || deletedView?.type || '';

      openSnackbar({
        text: `${deletedType[0]?.toUpperCase() + deletedType.slice(1)} view deleted`,
        snackType: 'standard',
        position: 'bottom-center',
      });
    }
  };

  const handleDeleteFile = async () => {
    closeExploreMenu();
    const response = await API.put('/files', { id: fileId, status: 'deleted' });
    if (response) {
      if (currentFile?.pin) fetchSidebar(mode);
      openSnackbar({
        text: `${currentFile?.name || 'File'} has been deleted`,
        snackType: 'standard',
        position: 'bottom-center',
      });

      if (currentFile?.workspace?.id) {
        router.push(`/${mode}/${orgId}/w/${currentFile?.workspace?.id}`);
      } else router.push(`/${mode}/${orgId}/dashboard`);
    }
  };

  const handleArchiveFile = async () => {
    const response = await API.put('/files', { id: fileId, status: 'archived' });
    if (response) {
      openSnackbar({
        text: `${currentFile?.name || 'File'} has been archived`,
        snackType: 'standard',
        position: 'bottom-center',
      });
      closeExploreMenu();
      if (currentFile?.pin) fetchSidebar(mode);
    }
  };
  const handleResetOnboarding = async () => {
    const response = await API.post('/settings/onboard', { type: 'reset-file' });
    if (response) {
      closeExploreMenu();
      fetchUserDetails();
    }
  };

  return (
    <div className="flex flex-col justify-between w-full text-gray-999 text-sm pb-12 ml-8">
      {currentFile?.pin ? (
        <button className="flex py-10" onClick={() => handlePinFile(!currentFile?.pin)}>
          <div className="flex">
            <div className="mt-6 mr-6">
              <PinFilledIcon className="block text-gray-500 text-sm" />
            </div>
            Unpin from sidebar
          </div>
        </button>
      ) : (
        <button className="flex py-10" onClick={() => handlePinFile(!currentFile?.pin)}>
          <div className="flex">
            <div className="mt-6 mr-6">
              <PinIcon className="block text-gray-500 text-sm" />
            </div>
            Pin to sidebar
          </div>
        </button>
      )}

      <button className="flex py-10" onClick={() => handleFavoriteFile(!currentFile?.favorite)}>
        <div className="flex">
          <div className="mt-1 mr-6">
            <StarFilledIcon className="block mt-6 text-yellow-500 text-sm" />
          </div>
        </div>
        <span>{currentFile?.favorite ? 'Remove from favorites' : 'Mark as favorite'}</span>
      </button>

      {/* <button className="flex py-10" onClick={() => console.log('')}>
        <div className="pt-2">
          <MoveIcon className="block text-gray-500" />
        </div>
        &nbsp;Move file
      </button> */}
      {/* <button className="flex py-10" onClick={() => console.log('')}>
        <div className="pt-2">
          <CopyIcon className="block text-gray-500" />
        </div>
        &nbsp;Create Copy of file
      </button> */}
      <Tippy
        content="You do not have the required permission to edit file properties"
        disabled={!isReadOnly}
      >
        <button
          className={classNames('flex py-10', {
            'opacity-50 cursor-not-allowed': isReadOnly,
          })}
          onClick={() => !isReadOnly && setIsRenameFileOpened(true)}
        >
          <div className="pt-2 mr-6">
            <RenameIcon wrapperClass="block text-gray-500" />
          </div>
          Rename file
        </button>
      </Tippy>
      <Tippy
        content="You do not have the required permission to archive file"
        disabled={!isReadOnly}
      >
        <button
          className={classNames('flex py-10', {
            'opacity-50 cursor-not-allowed': isReadOnly,
          })}
          onClick={isReadOnly ? undefined : handleArchiveFile}
        >
          <div className="pt-2 mr-6">
            <ArchiveIcon wrapperClass="block text-gray-500" />
          </div>
          Archive file
        </button>
      </Tippy>
      {router.query.type === 'list' && (
        <button className="flex py-10" onClick={() => handleResetOnboarding()}>
          <div className="pt-2">
            <FileIcon wrapperClass="block text-gray-500" />
          </div>
          &nbsp;Reset file onboarding
        </button>
      )}
      {currentFile?.primaryFile?.id !== viewId && (
        <Tippy
          content="You do not have the required permission to delete view"
          disabled={!isReadOnly}
        >
          <button
            className={classNames('flex py-10', {
              'opacity-50 cursor-not-allowed': isReadOnly,
            })}
            onClick={isReadOnly ? undefined : handleDeleteView}
          >
            <div className="pt-2">
              <DeleteIcon className="block text-gray-500" />
            </div>
            &nbsp;Delete view
          </button>
        </Tippy>
      )}
      <Tippy
        content="You do not have the required permission to delete file"
        disabled={!isReadOnly}
      >
        <button
          className={classNames('flex py-10 text-red-500', {
            'opacity-50 cursor-not-allowed': isReadOnly,
          })}
          onClick={() => !isReadOnly && setisDeleteFilesModalOpen(true)}
        >
          <div className="pt-2">
            <DeleteIcon className="block text-red-500" />
          </div>
          &nbsp;Delete file
        </button>
      </Tippy>

      <Modal
        isOpen={isDeleteFilesModalOpen}
        onClose={() => {
          setisDeleteFilesModalOpen(false);
        }}
      >
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="font-semibold mb-16 text-2xl">Delete file?</p>
          <p className="mb-32 text-sm text-gray-999">
            Are you sure you want to delete{' '}
            <span className="font-medium">{currentFile?.name || 'File'}</span>
            <span className="font-medium">{} </span>?
            <br />
            <br />
            If you change your mind, you can get this file back by filtering for deleted{' '}
          </p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setisDeleteFilesModalOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleDeleteFile}
            >
              Delete
            </button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};
